'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { classnames } from '@/utils/classnames'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import { Arrow3Icon } from '@/components/icon/icons'
import s from './Navbar.module.scss'

const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/'

type NavItemWithSubmenuProps = {
  text: string
  href: string
  active: boolean
  hasActiveChild?: boolean
  submenu: { text: string; handle: string; href?: string }[]
  onSubmenuClick?: () => void
}

const NavItemWithSubmenu = ({
  text,
  href,
  active = false,
  hasActiveChild = false,
  submenu,
  onSubmenuClick,
}: NavItemWithSubmenuProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Zamknij dropdown przy zmianie strony
  useEffect(() => {
    setIsExpanded(false)
  }, [pathname])

  // Wykryj czy to mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024) // lg breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Zamknij dropdown przy kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleMouseEnter = () => {
    // Hover tylko na desktop
    if (!isMobile) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      setIsExpanded(true)
    }
  }

  const handleMouseLeave = () => {
    // Hover tylko na desktop
    if (!isMobile) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false)
      }, 150) // 150ms delay
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      className={classnames(s.dropdownContainer, {
        [s.mobile]: isMobile,
        [s.desktop]: !isMobile,
      })}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ButtonOrLink
        as="button"
        variant="menuLink"
        classes={classnames(s.linkContainer, s.pulseLink)}
        buttonColor="transparent"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span
          className={s.linkContent}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <div
            className={classnames(s.dot, {
              [s.active]: active || hasActiveChild,
            })}
          />
          <span className={classnames(s.link, { [s.active]: active })}>
            {text}
          </span>
          <Arrow3Icon
            width={12}
            height={12}
            className={classnames(s.dropdownArrow, {
              [s.expanded]: isExpanded,
            })}
          />
        </span>
      </ButtonOrLink>

      {isExpanded && (
        <div className={s.submenu}>
          {submenu.map((item) => {
            const isItemActive = pathname.startsWith(`/${item.handle}`)
            const itemHref = item.href ?? `/${item.handle}`
            return (
              <Link
                key={item.handle}
                href={itemHref}
                className={`${s.submenuItem} ${
                  isItemActive ? s.submenuItemActive : ''
                }`}
                onClick={(e) => {
                  if (normalizePath(pathname) === normalizePath(itemHref)) {
                    e.preventDefault()
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                  onSubmenuClick?.()
                }}
              >
                {item.text}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default NavItemWithSubmenu
